import { Injectable } from '@angular/core';
import { ofType, Actions, createEffect } from '@ngrx/effects';
import { mergeMap, map, catchError, tap } from 'rxjs/operators';
import { of } from 'rxjs';
import {
  SkillsErroredAction,
  SkillsLoadedAction,
  KnowledgeConfigInitialized,
  UpdateSkillAction,
  DeleteSkillAction,
  DeleteSkillSuccessAction,
  CreateSkillAction,
  SkillsUsageLoadedAction,
  LoadSkillsUsageAction,
  LoadSkillsAsOrgUserAction,
  SkillsLoadedAsOrgUserAction,
  LoadSkillAction,
  LoadSkillSucceededAction,
  GoToSkillDetailPageAction,
  LoadSkillErroredAction,
} from './skills.action';
import { SkillService } from './skills.service';
import { SkillApiError, SkillApiErrorTypes } from './skills.errors';
import { getSkillsErrorType } from './helpers/skills-error.util';
import { Router } from '@angular/router';

@Injectable()
export class SkillsEffects {
  getSkillsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(KnowledgeConfigInitialized),
      mergeMap(() =>
        this.skillService.getSkillsList().pipe(
          map((skills) => SkillsLoadedAction({ skills })),
          catchError((err) =>
            of(
              SkillsErroredAction({
                error: new SkillApiError(getSkillsErrorType(SkillApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  createSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CreateSkillAction),
      mergeMap((action) =>
        this.skillService.createSkill(action.values).pipe(
          map(() => GoToSkillDetailPageAction()),
          catchError((err) =>
            of(
              SkillsErroredAction({
                error: new SkillApiError(getSkillsErrorType(SkillApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  updateSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UpdateSkillAction),
      mergeMap((action) =>
        this.skillService.updateSkill(action.values, action.id).pipe(
          map(() => GoToSkillDetailPageAction()),
          catchError((err) =>
            of(
              SkillsErroredAction({
                error: new SkillApiError(getSkillsErrorType(SkillApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  deleteSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(DeleteSkillAction),
      mergeMap((action) =>
        this.skillService.deleteSkill(action.id).pipe(
          map(() => DeleteSkillSuccessAction({ id: action.id })),
          catchError((err) =>
            of(
              SkillsErroredAction({
                error: new SkillApiError(getSkillsErrorType(SkillApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadSkillsAsOrgUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSkillsAsOrgUserAction),
      mergeMap(() =>
        this.skillService.loadSkillsAsOrgUser().pipe(
          map((skills) => SkillsLoadedAsOrgUserAction({ skills })),
          catchError((err) =>
            of(
              SkillsErroredAction({
                error: new SkillApiError(getSkillsErrorType(SkillApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  goToManageSkillPage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(GoToSkillDetailPageAction),
        tap(() => {
          this.router.navigate(['manage-skills']);
        }),
      ),
    { dispatch: false },
  );

  getSkillsUsage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSkillsUsageAction),
      mergeMap(() =>
        this.skillService.getSkillsUsage().pipe(
          map((skillsUsage) => SkillsUsageLoadedAction({ skillsUsage })),
          catchError((err) =>
            of(
              SkillsErroredAction({
                error: new SkillApiError(getSkillsErrorType(SkillApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  loadSkill$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadSkillAction),
      mergeMap(({ id }) =>
        this.skillService.getSkillById(id).pipe(
          map((skill) => LoadSkillSucceededAction({ skill })),
          catchError((err) =>
            of(
              LoadSkillErroredAction({
                error: new SkillApiError(getSkillsErrorType(SkillApiErrorTypes, err.status), err),
              }),
            ),
          ),
        ),
      ),
    ),
  );

  constructor(
    private actions$: Actions,
    private skillService: SkillService,
    private router: Router,
  ) {}
}
